/**
 * The single Google Maps JS API loader for the whole SPA.
 *
 * Before this there were six `<LoadScript>` mounts plus a hand-rolled injector in
 * LatLngPicker, and they disagreed about `libraries`: the activity editor asked for
 * `places`, everything else asked for nothing. Two different script URLs in one SPA
 * session is a genuine double-include, which is what Google's "You have included the
 * Google Maps JavaScript API multiple times" warning was about. LoadScript also has no
 * hook for `loading=async` and no way to pin `v=`, so a Google-side channel rollover
 * could break the app with no local warning.
 *
 * Everything goes through `loadGoogleMaps()`, which resolves once and returns the same
 * promise thereafter. `importLibrary` works with this direct script tag once Google's
 * callback has fired, so callers ask for the library they need at the point they
 * need it rather than declaring it in a URL.
 *
 * `loading=async` and a `libraries=` query param cannot be combined, and the
 * script element's load event fires before `google.maps.Map` exists. Resolving
 * there makes the first `new google.maps.Map` throw ("Loader.provide not called
 * by module 'util'", or Map is not a constructor). The dashboard has no error
 * boundary, so that throw in the map's mount effect unmounts the whole SPA —
 * CP-8395, a blank Line Set editor as soon as a line (and therefore its map) is
 * on screen. Readiness is Google's callback, then `importLibrary`; the promise
 * resolves only once `Map` is a real constructor.
 *
 * See aidocs/lessons.md #1 for the LoadScript conflicts this replaces.
 */

// Quarterly rather than the weekly default: the app is not pinned to any version
// today, so a Google channel rollover lands unannounced. Quarterly still receives
// every release, just on a slower cadence that leaves time to react.
const MAPS_VERSION = 'quarterly';

// Imported once Google's callback fires. The classic namespace (Map, Marker,
// Polyline, Size, ...) is already present by then; importing `maps` only
// guarantees it, and `places` is preloaded so Place Search skips a round trip.
// The separate `marker` library is AdvancedMarkerElement, which this app does not
// use. Requesting it on the URL showed up in Sentry as "Loader.provide not called
// by module 'marker'".
const CORE_LIBRARIES = ['maps', 'places'];

const SCRIPT_ID = 'google-maps-js-api';

// Google invokes this once the core API is actually usable. With loading=async
// the script element's own load event fires first, while Map and importLibrary
// are still missing — resolving there is what constructed a map too early.
const CALLBACK_NAME = '__cpGoogleMapsLoaded';

let loadPromise = null;
let readyTimer = null;

const clearReadyTimer = () => {
    if (readyTimer) {
        clearInterval(readyTimer);
        readyTimer = null;
    }
};

const scriptUrl = (apiKey) => {
    const params = new URLSearchParams({
        key: apiKey,
        v: MAPS_VERSION,
        loading: 'async',
        callback: CALLBACK_NAME,
    });
    return `https://maps.googleapis.com/maps/api/js?${params.toString()}`;
};

const mapsReady = () => typeof window.google?.maps?.Map === 'function';

// Do not resolve until Map is a real constructor.
const ensureCoreLibraries = async () => {
    const maps = window.google && window.google.maps;
    if (maps && typeof maps.importLibrary === 'function') {
        for (const name of CORE_LIBRARIES) {
            await maps.importLibrary(name);
        }
    }
    if (!mapsReady()) {
        throw new Error('Google Maps loaded but Map is not available');
    }
    return window.google.maps;
};

const settle = (resolve, reject) => {
    ensureCoreLibraries().then(resolve, (err) => {
        loadPromise = null;
        reject(err);
    });
};

/**
 * Resolve once `window.google.maps` is usable. Safe to call from anywhere, any number
 * of times, with or without a key already loaded.
 *
 * @param {string} apiKey settings.google_maps_key
 * @returns {Promise<typeof window.google.maps>}
 */
export const loadGoogleMaps = (apiKey) => {
    // importLibrary existing is not enough: the async bootstrap installs that
    // function before Map is a constructor. Resolving there is what let the
    // line-set map throw during mount.
    if (mapsReady()) return Promise.resolve(window.google.maps);
    if (loadPromise) return loadPromise;

    if (typeof window.google?.maps?.importLibrary === 'function') {
        loadPromise = new Promise(settle);
        return loadPromise;
    }

    // A tag may already exist from a previous mount, or from a build that still uses
    // LoadScript. Wait for it rather than adding a second one.
    const existing = document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`);
    if (existing) {
        loadPromise = new Promise((resolve, reject) => {
            const started = Date.now();
            const check = () => {
                if (window.google?.maps?.importLibrary || mapsReady()) return settle(resolve, reject);
                // Bounded: a tag that never resolves must not leave callers hanging
                // forever on a timer that also never stops.
                if (Date.now() - started > 20000) {
                    loadPromise = null;
                    return reject(new Error('Google Maps script present but never initialised'));
                }
                setTimeout(check, 50);
            };
            check();
        });
        return loadPromise;
    }

    if (!apiKey) return Promise.reject(new Error('No Google Maps API key'));

    loadPromise = new Promise((resolve, reject) => {
        const started = Date.now();
        readyTimer = setInterval(() => {
            if (Date.now() - started <= 20000) return;
            clearReadyTimer();
            delete window[CALLBACK_NAME];
            loadPromise = null;
            reject(new Error('Google Maps script present but never initialised'));
        }, 250);
        const finish = (settleFn) => {
            clearReadyTimer();
            delete window[CALLBACK_NAME];
            settleFn();
        };
        window[CALLBACK_NAME] = () => finish(() => settle(resolve, reject));
        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = scriptUrl(apiKey);
        script.async = true;
        script.defer = true;
        script.onerror = () => finish(() => {
            // Clear the cached promise so a later mount can retry rather than
            // inheriting a permanently rejected one.
            loadPromise = null;
            reject(new Error('Failed to load Google Maps'));
        });
        document.head.appendChild(script);
    });
    return loadPromise;
};

/**
 * Load the API and one of its libraries.
 *
 * `importLibrary` is the modern replacement for the `libraries=` URL parameter and is
 * what the new Places classes (Place, AutocompleteSuggestion) are published through.
 *
 * @param {string} name e.g. 'places', 'marker', 'maps'
 * @param {string} apiKey settings.google_maps_key
 */
export const importMapsLibrary = async (name, apiKey) => {
    const maps = await loadGoogleMaps(apiKey);
    return maps.importLibrary(name);
};

/** Test seam: forget the cached promise. Not for application code. */
export const __resetGoogleMapsLoader = () => {
    loadPromise = null;
    clearReadyTimer();
    delete window[CALLBACK_NAME];
};
